import { __ } from '@wordpress/i18n';
import { useBlockProps, InspectorControls } from '@wordpress/block-editor';
import { PanelBody, SelectControl, ComboboxControl, Placeholder, Spinner } from '@wordpress/components';
import { useState, useEffect } from '@wordpress/element';
import useStyles from '../../js/hooks/useStyles';

export default function Edit({ attributes, setAttributes }) {
    const { projectToplistId, style } = attributes;
    const [toplists, setToplists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Fetch available styles for toplist blocks
    const { styles: styleOptions, loading: stylesLoading } = useStyles('toplist');

    // Fetch project toplists with full data for preview
    useEffect(() => {
        fetch('/wp-json/cas/v1/project-toplists')
            .then(response => {
                if (!response.ok) {
                    throw new Error('Failed to fetch toplists');
                }
                return response.json();
            })
            .then(data => {
                if (data.success && Array.isArray(data.data)) {
                    setToplists(data.data);
                    // console.log('Fetched toplists:', data.data);
                } else {
                    setError('No toplists available');
                }
                setLoading(false);
            })
            .catch(err => {
                setError(err.message);
                setLoading(false);
            });
    }, []);

    // Get the selected toplist data for preview
    const selectedToplist = toplists.find(toplist => toplist.uuid === projectToplistId);

    const blockProps = useBlockProps({
        className: 'wp-block-cas-project-toplist'
    });

    if (loading) {
        return (
            <div {...blockProps}>
                <Placeholder 
                    icon="list-view" 
                    label={__('CAS Project Toplist', 'cas-plugin')}
                    className="cas-block-placeholder"
                >
                    <Spinner />
                    <p>{__('Loading toplists...', 'cas-plugin')}</p>
                </Placeholder>
            </div>
        );
    }

    if (error) {
        return (
            <div {...blockProps}>
                <Placeholder 
                    icon="warning" 
                    label={__('CAS Project Toplist', 'cas-plugin')}
                    className="cas-block-placeholder"
                >
                    <p>{__('Error loading toplists: ', 'cas-plugin') + error}</p>
                </Placeholder>
            </div>
        );
    }

    if (!projectToplistId) {
        return (
            <>
                <div {...blockProps}>
                    <Placeholder 
                        icon="list-view" 
                        label={__('CAS Project Toplist', 'cas-plugin')}
                        instructions={__('Search and select a project toplist to display', 'cas-plugin')}
                        className="cas-block-placeholder"
                    >
                        <ComboboxControl
                            value={projectToplistId}
                            options={toplists.map(toplist => ({
                                label: `${toplist.name || 'Unnamed Toplist'}${toplist.market_name ? ` (${toplist.market_name})` : ''}`,
                                value: toplist.uuid
                            }))}
                            onChange={(value) => {
                                setAttributes({
                                    projectToplistId: value
                                });
                            }}
                            placeholder={__('Type to search toplists...', 'cas-plugin')}
                        />
                    </Placeholder>
                </div>
                <InspectorControls>
                    <PanelBody title={__('Toplist Settings', 'cas-plugin')} className="cas-block-inspector">
                        <SelectControl
                            label={__('Style', 'cas-plugin')}
                            value={style}
                            options={styleOptions}
                            onChange={(value) => setAttributes({ style: value })}
                            disabled={stylesLoading}
                            help={stylesLoading ? __('Loading available styles...', 'cas-plugin') : __('Choose a style template for this toplist.', 'cas-plugin')}
                        />
                    </PanelBody>
                </InspectorControls>
            </>
        );
    }

    // Client-side preview with real data - using offer-style cards for each listing!
    return (
        <>
            <div {...blockProps}>
                {selectedToplist ? (
                    <div className="cas-toplist-container">
                        <div className="cas-toplist-header">
                            <div className="cas-toplist-name">
                                {selectedToplist.name || 'Casino Toplist'}
                            </div>
                        </div>
                        <div className="cas-toplist-cards">
                            {selectedToplist.listings && selectedToplist.listings.length > 0 ? (
                                selectedToplist.listings.map((listing, index) => (
                                    <div key={index} className="cas-offer-skeleton">
                                        <div className="cas-toplist-rank">#{index + 1}</div>
                                        <div 
                                            className="cas-logo-placeholder"
                                            style={{ 
                                                backgroundColor: listing.brand_logo_background_color || '#f8f9fa'
                                            }}
                                        >
                                            {listing.brand_logo_url ? (
                                                <img 
                                                    src={listing.brand_logo_url} 
                                                    alt={listing.brand_name || 'Brand Logo'}
                                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                                />
                                            ) : (
                                                <span style={{ fontSize: '10px', color: '#6c757d' }}>LOGO</span>
                                            )}
                                        </div>
                                        <div className="cas-offer-content">
                                            <div 
                                                className="cas-offer-title"
                                                dangerouslySetInnerHTML={{
                                                    __html: listing.offer_title_highlighted || listing.offer_title || 'Special Offer'
                                                }}
                                            />
                                        </div>
                                        <div className="cas-cta-placeholder">
                                            {listing.cta_text || 'Visit Site'}
                                        </div>
                                        <div 
                                            className="cas-offer-terms"
                                            dangerouslySetInnerHTML={{
                                                __html: listing.offer_terms || 'T&Cs apply. 18+. Gamble responsibly.'
                                            }}
                                        />
                                    </div>
                                ))
                            ) : (
                                <div className="cas-offer-skeleton">
                                    <div className="cas-offer-content">
                                        <div className="cas-offer-title">No listings available</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <Placeholder 
                        icon="warning" 
                        label={__('CAS Project Toplist', 'cas-plugin')}
                        className="cas-block-placeholder"
                    >
                        <p>{__('Selected toplist not found. Please choose a different toplist.', 'cas-plugin')}</p>
                    </Placeholder>
                )}
            </div>
            <InspectorControls>
                <PanelBody title={__('Toplist Settings', 'cas-plugin')} className="cas-block-inspector">
                    <ComboboxControl
                        label={__('Search Project Toplists', 'cas-plugin')}
                        value={projectToplistId}
                        options={toplists.map(toplist => ({
                            label: `${toplist.name || 'Unnamed Toplist'}${toplist.market_name ? ` (${toplist.market_name})` : ''}`,
                            value: toplist.uuid
                        }))}
                        onChange={(value) => {
                            setAttributes({
                                projectToplistId: value
                            });
                        }}
                        allowReset={true}
                        placeholder={__('Type to search toplists...', 'cas-plugin')}
                        help={__('Search by toplist name or market to quickly find the toplist you want to display.', 'cas-plugin')}
                    />
                    <SelectControl
                        label={__('Style', 'cas-plugin')}
                        value={style}
                        options={styleOptions}
                        onChange={(value) => setAttributes({ style: value })}
                        disabled={stylesLoading}
                        help={stylesLoading ? __('Loading available styles...', 'cas-plugin') : __('Choose a style template for this toplist.', 'cas-plugin')}
                    />
                </PanelBody>
            </InspectorControls>
        </>
    );
}
